<template>
  <div class="deu-reporte">

    <!-- Encabezado -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="deu-titulo mb-0">
        <i class="bi bi-people me-2"></i>Cuentas por Cobrar — Crédito
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="deu-periodo-badge">{{ reporte.periodo.etiqueta }}</span>
        <template v-if="reporte && !cargando">
          <button
            class="btn btn-sm btn-outline-success"
            @click="exportarXlsx"
            :disabled="exportandoXlsx"
            title="Descargar Excel"
          >
            <span v-if="exportandoXlsx" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-excel me-1"></i>Excel
          </button>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="exportarPdf"
            :disabled="exportandoPdf"
            title="Descargar PDF"
          >
            <span v-if="exportandoPdf" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-file-earmark-pdf me-1"></i>PDF
          </button>
        </template>
      </div>
    </div>

    <!-- Panel filtros -->
    <div class="deu-panel mb-3">
      <div class="deu-panel__cuerpo">

        <!-- Toggle modo -->
        <div class="d-flex gap-2 mb-3">
          <button
            :class="['btn btn-sm', modoFiltro === 'anio' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'anio'"
          ><i class="bi bi-calendar2-range me-1"></i>Por año</button>
          <button
            :class="['btn btn-sm', modoFiltro === 'rango' ? 'btn-primary' : 'btn-outline-secondary']"
            @click="modoFiltro = 'rango'"
          ><i class="bi bi-calendar3 me-1"></i>Rango de fechas</button>
        </div>

        <!-- Selector por año -->
        <div v-if="modoFiltro === 'anio'" class="d-flex flex-wrap gap-2 align-items-center">
          <span class="deu-label-muted me-1">Seleccionar año:</span>
          <button
            v-for="a in aniosDisponibles"
            :key="a"
            :class="['btn btn-sm', anioSeleccionado === a ? 'btn-primary' : 'btn-outline-secondary']"
            @click="seleccionarAnio(a)"
            :disabled="cargando"
          >{{ a }}</button>
        </div>

        <!-- Rango personalizado -->
        <div v-if="modoFiltro === 'rango'" class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha desde</label>
            <input type="date" class="form-control form-control-sm deu-input" v-model="filtros.fecha_desde" :max="filtros.fecha_hasta" />
          </div>
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Fecha hasta</label>
            <input type="date" class="form-control form-control-sm deu-input" v-model="filtros.fecha_hasta" :min="filtros.fecha_desde" />
          </div>
          <div class="col-auto d-flex gap-1">
            <button class="btn btn-sm btn-primary" @click="generarReporte" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>{{ cargando ? 'Generando…' : 'Generar' }}
            </button>
            <button class="btn btn-sm btn-link" style="color:var(--text-muted)" @click="limpiarRango" :disabled="cargando">
              Limpiar
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMensaje" class="deu-alerta deu-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="deu-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Generando reporte…
    </div>

    <template v-if="reporte && !cargando">

      <!-- KPI cards -->
      <div class="row g-3 mb-3">
        <div class="col-sm-6 col-xl">
          <div class="deu-kpi">
            <div class="deu-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.clientes_distintos }}</div>
            <div class="deu-kpi__etiqueta">Clientes distintos</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="deu-kpi">
            <div class="deu-kpi__valor" style="color:var(--primary-color)">{{ reporte.resumen.num_facturas }}</div>
            <div class="deu-kpi__etiqueta">Facturas a crédito</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="deu-kpi">
            <div class="deu-kpi__valor" style="color:var(--accent-success)">{{ formatearMoneda(reporte.resumen.total_deuda) }}</div>
            <div class="deu-kpi__etiqueta">Total emitido a crédito</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="deu-kpi">
            <div class="deu-kpi__valor" style="color:var(--accent-warning)">{{ formatearMoneda(reporte.resumen.total_saldo) }}</div>
            <div class="deu-kpi__etiqueta">Saldo pendiente</div>
          </div>
        </div>
        <div class="col-sm-6 col-xl">
          <div class="deu-kpi">
            <div
              class="deu-kpi__valor"
              :style="reporte.resumen.total_vencido > 0 ? 'color:var(--accent-danger)' : 'color:var(--text-muted)'"
            >
              {{ formatearMoneda(reporte.resumen.total_vencido) }}
            </div>
            <div class="deu-kpi__etiqueta">Total vencido</div>
          </div>
        </div>
      </div>

      <!-- Resumen por año -->
      <div v-if="reporte.resumen_anual.length > 0" class="deu-panel mb-3">
        <div class="deu-panel__cabecera">
          <i class="bi bi-bar-chart me-2"></i>Resumen por año
        </div>
        <div class="table-responsive">
          <table class="deu-tabla">
            <thead>
              <tr>
                <th>Año</th>
                <th class="text-end">Facturas</th>
                <th class="text-end">Clientes</th>
                <th class="text-end">Total crédito</th>
                <th class="text-end">Saldo pendiente</th>
                <th class="text-end">Total vencido</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in reporte.resumen_anual"
                :key="fila.anio"
                class="deu-fila"
                :class="{ 'deu-fila--activa': esFilaActiva(fila.anio) }"
              >
                <td>
                  <span class="fw-semibold">{{ fila.anio }}</span>
                  <span v-if="esFilaActiva(fila.anio)" class="deu-badge-sel ms-1">seleccionado</span>
                </td>
                <td class="text-end">{{ fila.num_facturas }}</td>
                <td class="text-end">{{ fila.clientes_distintos }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ formatearMoneda(fila.total_deuda) }}</td>
                <td class="text-end fw-semibold" style="color:var(--accent-warning)">{{ formatearMoneda(fila.total_saldo) }}</td>
                <td class="text-end">
                  <span v-if="fila.total_vencido > 0" class="deu-badge-vencido">{{ formatearMoneda(fila.total_vencido) }}</span>
                  <span v-else class="deu-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tabla de facturas -->
      <div class="deu-panel mb-3">
        <div class="deu-panel__cabecera d-flex align-items-center justify-content-between">
          <span><i class="bi bi-receipt me-2"></i>Facturas a crédito — {{ reporte.periodo.etiqueta }}</span>
          <span class="deu-conteo">{{ reporte.facturas.length }} factura(s)</span>
        </div>
        <div v-if="reporte.facturas.length > 0" class="table-responsive">
          <table class="deu-tabla">
            <thead>
              <tr>
                <th style="width:38px">#</th>
                <th>Fecha emis.</th>
                <th>N.° Factura</th>
                <th>Cliente</th>
                <th>Identificación</th>
                <th class="text-center">Plazo</th>
                <th class="text-center">Vence</th>
                <th class="text-end">Total</th>
                <th class="text-end">Abonado</th>
                <th class="text-end">Saldo pendiente</th>
                <th class="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(f, idx) in reporte.facturas"
                :key="f.id"
                class="deu-fila"
                :class="{ 'deu-fila--vencida': f.vencida }"
              >
                <td class="text-center deu-muted">{{ idx + 1 }}</td>
                <td>{{ f.fecha_emision }}</td>
                <td class="fw-semibold" style="font-family:monospace">{{ f.numero_factura }}</td>
                <td>{{ f.cliente }}</td>
                <td class="deu-muted">{{ f.identificacion }}</td>
                <td class="text-center deu-muted">{{ f.dias_plazo_credito }}d</td>
                <td class="text-center">{{ f.fecha_vencimiento_credito || '—' }}</td>
                <td class="text-end" style="color:var(--text-secondary)">{{ formatearMoneda(f.total) }}</td>
                <td class="text-end" style="color:var(--accent-warning)">
                  {{ f.monto_abonado > 0 ? formatearMoneda(f.monto_abonado) : '—' }}
                </td>
                <td
                  class="text-end fw-semibold"
                  :style="f.vencida ? 'color:var(--accent-danger)' : 'color:var(--accent-success)'"
                >
                  {{ formatearMoneda(f.saldo_pendiente) }}
                </td>
                <td class="text-center">
                  <span v-if="f.vencida" class="deu-badge deu-badge--vencida">
                    <i class="bi bi-exclamation-triangle-fill me-1"></i>Vencida ({{ f.dias_vencido }}d)
                  </span>
                  <span v-else class="deu-badge deu-badge--vigente">
                    <i class="bi bi-check-circle-fill me-1"></i>Vigente
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="deu-pie">
                <td colspan="7">Total</td>
                <td class="text-end" style="color:var(--text-secondary)">{{ formatearMoneda(reporte.resumen.total_deuda) }}</td>
                <td></td>
                <td class="text-end" style="color:var(--accent-warning)">{{ formatearMoneda(reporte.resumen.total_saldo) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="deu-estado-vacio">
          <i class="bi bi-people fs-3 d-block mb-2"></i>
          No hay facturas emitidas a crédito en este período.
          <div class="mt-2" style="font-size:.85rem">
            Para que aparezcan aquí, activa la opción <strong>"¿Es a crédito?"</strong> al emitir una factura.
          </div>
        </div>
      </div>

    </template>

    <!-- Estado inicial -->
    <div v-if="!reporte && !cargando && !errorMensaje" class="deu-panel">
      <div class="deu-estado-vacio">
        <i class="bi bi-people fs-2 d-block mb-2"></i>
        Selecciona un año o rango de fechas para ver las facturas a crédito.
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import * as XLSX from 'xlsx'

export default {
  name: 'ClientReportesClientesDeuda',

  data() {
    const hoy = new Date()
    return {
      modoFiltro: 'anio',
      anioSeleccionado: hoy.getFullYear(),
      filtros: {
        fecha_desde: `${hoy.getFullYear()}-01-01`,
        fecha_hasta: hoy.toISOString().slice(0, 10),
      },
      cargando: false,
      errorMensaje: null,
      reporte: null,
      exportandoXlsx: false,
      exportandoPdf: false,
    }
  },

  mounted() {
    this.generarReporte()
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },

    aniosDisponibles() {
      const actual = new Date().getFullYear()
      const set = new Set([actual])
      if (this.reporte?.resumen_anual) {
        this.reporte.resumen_anual.forEach(r => set.add(Number(r.anio)))
      }
      return Array.from(set).sort((a, b) => b - a)
    },
  },

  methods: {
    async generarReporte() {
      this.cargando = true
      this.errorMensaje = null
      this.reporte = null
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const { data } = await api.get(`/reportes/clientes-deuda?${params}`)
        if (data.success) {
          this.reporte = data
        } else {
          this.errorMensaje = data.error || 'No se pudieron obtener los datos.'
        }
      } catch (err) {
        this.errorMensaje = err?.response?.data?.error || 'Error al conectar con el servidor.'
      } finally {
        this.cargando = false
      }
    },

    seleccionarAnio(anio) {
      this.anioSeleccionado = anio
      this.generarReporte()
    },

    limpiarRango() {
      const hoy = new Date()
      this.filtros.fecha_desde = `${hoy.getFullYear()}-01-01`
      this.filtros.fecha_hasta = hoy.toISOString().slice(0, 10)
      this.reporte = null
      this.errorMensaje = null
    },

    esFilaActiva(anio) {
      return this.modoFiltro === 'anio' && Number(anio) === this.anioSeleccionado
    },

    formatearMoneda(valor) {
      return '$' + parseFloat(valor || 0).toFixed(2)
    },

    // ── Exportar Excel ────────────────────────────────────────────────
    exportarXlsx() {
      if (!this.reporte) return
      this.exportandoXlsx = true
      try {
        const r  = this.reporte
        const wb = XLSX.utils.book_new()

        // Hoja Resumen
        const resumen = [
          ['Reporte de Cuentas por Cobrar — Crédito'],
          ['Período', r.periodo.etiqueta],
          [],
          ['Concepto', 'Valor'],
          ['Clientes distintos',      r.resumen.clientes_distintos],
          ['Facturas a crédito',      r.resumen.num_facturas],
          ['Total emitido a crédito', r.resumen.total_deuda],
          ['Saldo pendiente',          r.resumen.total_saldo],
          ['Total vencido',            r.resumen.total_vencido],
        ]
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen')

        // Hoja por año
        if (r.resumen_anual.length > 0) {
          const cabA = ['Año', 'Facturas', 'Clientes', 'Total crédito', 'Saldo pendiente', 'Total vencido']
          const filA = r.resumen_anual.map(f => [
            f.anio, f.num_facturas, f.clientes_distintos,
            parseFloat(f.total_deuda), parseFloat(f.total_saldo), parseFloat(f.total_vencido),
          ])
          XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabA, ...filA]), 'Por año')
        }

        // Hoja facturas
        const cabF = ['Fecha emisión', 'N.° Factura', 'Cliente', 'Identificación', 'Días plazo', 'Fecha vencimiento', 'Total', 'Abonado', 'Saldo pendiente', 'Estado']
        const filF = r.facturas.map(f => [
          f.fecha_emision,
          f.numero_factura,
          f.cliente,
          f.identificacion,
          f.dias_plazo_credito,
          f.fecha_vencimiento_credito || '',
          parseFloat(f.total),
          parseFloat(f.monto_abonado),
          parseFloat(f.saldo_pendiente),
          f.vencida ? `Vencida (${f.dias_vencido}d)` : 'Vigente',
        ])
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabF, ...filF]), 'Facturas')

        const etiq = r.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        XLSX.writeFile(wb, `clientes_deuda_${etiq}.xlsx`)
      } finally {
        this.exportandoXlsx = false
      }
    },

    // ── Exportar PDF ──────────────────────────────────────────────────
    async exportarPdf() {
      if (!this.reporte) return
      this.exportandoPdf = true
      try {
        const params = new URLSearchParams()
        if (this.modoFiltro === 'anio') {
          params.set('anio', this.anioSeleccionado)
        } else {
          if (this.filtros.fecha_desde) params.set('fecha_desde', this.filtros.fecha_desde)
          if (this.filtros.fecha_hasta) params.set('fecha_hasta', this.filtros.fecha_hasta)
        }
        const response = await api.get(
          `/reportes/clientes-deuda/pdf?${params}`,
          { responseType: 'blob' }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = URL.createObjectURL(blob)
        const a   = document.createElement('a')
        const etiq = this.reporte.periodo.etiqueta.replace(/[^a-z0-9]/gi, '_')
        a.href     = url
        a.download = `clientes_deuda_${etiq}.pdf`
        a.click()
        URL.revokeObjectURL(url)
      } catch {
        this.errorMensaje = 'No se pudo generar el PDF. Intente nuevamente.'
      } finally {
        this.exportandoPdf = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ── */
.deu-reporte {
  padding: 1.25rem;
}

.deu-titulo {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--primary-color);
}

.deu-periodo-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--secondary-bg-soft);
  border: 1px solid var(--border-main);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
}

/* ── Panel ── */
.deu-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.deu-panel__cuerpo {
  padding: 0.85rem 1rem;
}

.deu-panel__cabecera {
  padding: 0.6rem 0.85rem;
  background: var(--secondary-bg-soft);
  border-bottom: 1px solid var(--border-main);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

/* ── KPI ── */
.deu-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  text-align: center;
  padding: 1rem;
}

.deu-kpi__valor {
  font-size: 1.5rem;
  font-weight: 700;
}

.deu-kpi__etiqueta {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
}

/* ── Alertas ── */
.deu-alerta {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid;
}

.deu-alerta--error {
  background: var(--accent-danger-light);
  border-color: var(--accent-danger);
  color: var(--accent-danger);
}

/* ── Estado vacío ── */
.deu-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Tabla ── */
.deu-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.deu-tabla thead tr {
  background: var(--secondary-bg-soft);
  border-bottom: 2px solid var(--border-main);
}

.deu-tabla thead th {
  padding: 0.6rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.deu-fila {
  border-bottom: 1px solid var(--border-main);
  transition: background 0.1s;
}

.deu-fila td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  color: var(--text-primary);
}

.deu-fila:hover {
  background: var(--table-hover);
}

.deu-fila--activa {
  background: var(--accent-info-light) !important;
  border-left: 3px solid var(--primary-color);
}

.deu-fila--vencida {
  background: var(--accent-danger-light, rgba(220,53,69,0.06)) !important;
}

/* ── Pie tabla ── */
.deu-pie td {
  padding: 0.55rem 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--secondary-bg-soft);
  border-top: 2px solid var(--border-main);
}

/* ── Badges de estado ── */
.deu-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 12px;
  white-space: nowrap;
}

.deu-badge--vencida {
  background: var(--accent-danger-light, rgba(220,53,69,0.1));
  color: var(--accent-danger, #dc3545);
  border: 1px solid var(--accent-danger, #dc3545);
}

.deu-badge--vigente {
  background: var(--accent-success-light, rgba(40,167,69,0.1));
  color: var(--accent-success, #28a745);
  border: 1px solid var(--accent-success, #28a745);
}

.deu-badge-vencido {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent-danger, #dc3545);
  background: var(--accent-danger-light, rgba(220,53,69,0.08));
  padding: 0.15rem 0.5rem;
  border-radius: 8px;
}

.deu-badge-sel {
  font-size: 0.7rem;
  background: var(--accent-info-light);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  padding: 0.1rem 0.45rem;
  border-radius: 10px;
}

/* ── Auxiliares ── */
.deu-muted {
  color: var(--text-muted);
}

.deu-label-muted {
  font-size: 0.82rem;
  color: var(--text-muted);
}

.deu-conteo {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.deu-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}
</style>

